import { useState, useEffect, useRef, useCallback } from "react";

// Formatiert den heutigen Tag im gleichen Format wie die Turnier-Tage
// ("11. April"), sodass wir gegen daysOfWeek matchen koennen.
const formatTodayLabel = () => {
  const today = new Date();
  const d = today.getDate();
  const m = today.toLocaleString("de-DE", { month: "long" });
  return `${d}. ${m.charAt(0).toUpperCase() + m.slice(1)}`;
};

/**
 * Tag-Auswahl fuer Turnier-Seiten:
 * - Initial wird "heute" gewaehlt, falls in daysOfWeek enthalten, sonst der erste Tag.
 * - Wenn daysOfWeek nachgeladen / aktualisiert wird UND der Nutzer noch nichts manuell
 *   ausgewaehlt hat, synchronisiert sich die Auswahl automatisch auf heute.
 * - Wenn der aktuell gewaehlte Tag nicht mehr in daysOfWeek vorhanden ist (z.B. nach
 *   Gruppenwechsel), faellt die Auswahl auf heute bzw. den ersten Tag zurueck.
 * - Sobald der Nutzer manuell einen Tag waehlt, wird nicht mehr automatisch ueberschrieben.
 */
export const useDefaultTournamentDay = (daysOfWeek) => {
  const initialToday = formatTodayLabel();
  const [selectedDay, setSelectedDayRaw] = useState(() => {
    if (Array.isArray(daysOfWeek) && daysOfWeek.includes(initialToday)) {
      return initialToday;
    }
    return Array.isArray(daysOfWeek) && daysOfWeek.length > 0 ? daysOfWeek[0] : "";
  });
  const userTouched = useRef(false);

  useEffect(() => {
    if (!Array.isArray(daysOfWeek) || daysOfWeek.length === 0) return;
    const today = formatTodayLabel();

    if (!userTouched.current) {
      // Solange der Nutzer nichts manuell gewaehlt hat: immer auf heute (falls moeglich)
      // snappen, sobald die echte Tagesliste geladen wurde.
      const target = daysOfWeek.includes(today) ? today : daysOfWeek[0];
      setSelectedDayRaw((prev) => (prev === target ? prev : target));
      return;
    }

    // Nutzer hat bewusst einen Tag gewaehlt; nur korrigieren wenn er nicht mehr existiert.
    if (!daysOfWeek.includes(selectedDay)) {
      setSelectedDayRaw(daysOfWeek.includes(today) ? today : daysOfWeek[0]);
    }
  }, [daysOfWeek, selectedDay]);

  const setSelectedDay = useCallback((value) => {
    userTouched.current = true;
    setSelectedDayRaw(typeof value === "function" ? value : value);
  }, []);

  return [selectedDay, setSelectedDay];
};

export { formatTodayLabel };
